import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logEvent } from '../utils/analytics';
import './ExamsPage.css';
import '../pages/Dashboard.css';

const API_BASE_URL = '/api';

function ExamsPage() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [activeMenu, setActiveMenu] = useState('exams');

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (userData) {
      loadExams();
    }
  }, [userData]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredExams(exams);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = exams.filter(exam =>
        exam.name.toLowerCase().includes(query) ||
        (exam.description && exam.description.toLowerCase().includes(query))
      );
      setFilteredExams(filtered);

      // Track exam search
      if (searchQuery.length >= 3) {
        logEvent('Exams', 'exam_search_performed', {
          search_query: searchQuery,
          results_count: filtered.length,
        });
      }
    }
  }, [searchQuery, exams]);

  const checkSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/check-session`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.authenticated && data.data && data.data.doctor) {
        setUserData(data.data.doctor);
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error checking session:', error);
      navigate('/login');
    }
  };

  const loadExams = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/exams`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        // Filter exams based on country_studying
        const countryStudying = userData?.country_studying;
        const allExams = data.data || data.exams || [];
        let filtered = allExams;
        
        if (countryStudying) {
          filtered = filtered.filter(exam => {
            // If exam has no countries specified, show it
            if (!exam.countries || exam.countries === null) {
              return true;
            }
            // If exam has countries, check if user's country is included
            try {
              const countries = typeof exam.countries === 'string' 
                ? JSON.parse(exam.countries) 
                : exam.countries;
              return Array.isArray(countries) && countries.includes(countryStudying);
            } catch (e) {
              return true; // If parsing fails, show the exam
            }
          });
        }
        
        setExams(filtered);
        setFilteredExams(filtered);
      }
    } catch (error) {
      console.error('Error loading exams:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExamClick = (examId) => {
    const exam = exams.find(e => e.id === examId);

    logEvent('Exams', 'exam_clicked', {
      exam_id: examId,
      exam_name: exam?.name || 'Unknown',
    });

    navigate(`/exams/${examId}`);
  };

  const handleStartChat = async (e, examId) => {
    e.stopPropagation(); // Prevent card click

    const exam = exams.find(e => e.id === examId);

    logEvent('Exams', 'exam_start_chat_clicked', {
      exam_id: examId,
      exam_name: exam?.name || 'Unknown',
    });

    try {
      // Create a new chat
      const chatResponse = await fetch(`${API_BASE_URL}/student-chats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({})
      });

      const chatData = await chatResponse.json();
      if (chatData.success && chatData.chat) {
        const chatId = chatData.chat.id;

        // Apply exam filter to the chat
        await fetch(`${API_BASE_URL}/student-chats/${chatId}/messages/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            message: '',
            exam_ids: [examId]
          })
        });

        // Navigate to chat
        navigate(`/chat/${chatId}`);
      }
    } catch (error) {
      console.error('Error starting chat:', error);
    }
  };

  const handleViewBooks = (e, examId) => {
    e.stopPropagation(); // Prevent card click
    navigate(`/exams/${examId}`);
  };

  const handleMenuClick = (menuItem) => {
    setActiveMenu(menuItem);
    if (menuItem === 'home') {
      navigate('/dashboard');
    } else if (menuItem === 'ai-chat') {
      navigate('/chat');
    } else if (menuItem === 'exams') {
      navigate('/exams');
    } else if (menuItem === 'question-bank') {
      navigate('/question-bank');
    } else if (menuItem === 'feedback') {
      navigate('/feedback');
    } else if (menuItem === 'profile') {
      navigate('/edit-profile');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/doctor-auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const getMenuItems = () => {
    if (!userData) return [];
    
    if (userData.user_type === 'medical_student') {
      return [
        { id: 'home', label: 'Home', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'ai-chat', label: 'AI Chat', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'exams', label: 'Exams', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 19V5C4 3.89543 4.89543 3 6 3H19.4C19.7314 3 20 3.26863 20 3.6V16.7143" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 17L20 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 21L20 21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M6 21C4.89543 21 4 20.1046 4 19C4 17.8954 4.89543 17 6 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 7L15 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg> },
        { id: 'question-bank', label: 'Question Bank', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 20.2044 11.6839 19.4413 11.1213 18.8787C10.5587 18.3161 9.79565 18 9 18H2V3Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 20.2044 12.3161 19.4413 12.8787 18.8787C13.4413 18.3161 14.2044 18 15 18H22V3Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'profile', label: 'Profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> }
      ];
    } else {
      return [
        { id: 'home', label: 'Home', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'ai-chat', label: 'AI Chat', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { id: 'profile', label: 'Profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg> }
      ];
    }
  };

  if (isLoading) {
    return (
      <div className="exams-page">
        <div className="exams-loading">Loading exams...</div>
      </div>
    );
  }

  const menuItems = getMenuItems().filter(item => item.id !== 'profile');

  return (
    <div className="exams-page">
      {/* Top Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <img src="/logo.svg" alt="S.C.A.L.P.E.L. Logo" className="dashboard-logo-img" />
        </div>
        <nav className="dashboard-nav desktop-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-menu-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => handleMenuClick(item.id)}
            >
              <span className="nav-menu-icon">{item.icon}</span>
              <span className="nav-menu-label">{item.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} className="dashboard-logout-btn">
          <svg width="20" height="20" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
            <path d="M12 12H19M19 12L16 15M19 12L16 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
            <path d="M19 6V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V18" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
          </svg>
          <span className="logout-text">Logout</span>
        </button>
      </div>

      <div className="exams-container">
        <div className="exams-header">
          <h1 className="exams-title">Exams</h1>
          <p className="exams-subtitle">Select an exam to view available books and start studying</p>
        </div>

        <div className="exams-search-wrapper">
          <input
            type="text"
            className="exams-search-input"
            placeholder="Search exams by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <svg className="exams-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        {filteredExams.length === 0 ? (
          <div className="exams-empty">
            {searchQuery ? 'No exams found matching your search.' : 'No exams available.'}
          </div>
        ) : (
          <div className="exams-grid">
            {filteredExams.map((exam) => (
              <div
                key={exam.id}
                className="exam-card"
                onClick={() => handleExamClick(exam.id)}
              >
                <div className="exam-card-content">
                  <h3 className="exam-card-title">{exam.name}</h3>
                  {exam.description && (
                    <p className="exam-card-description">{exam.description}</p>
                  )}
                  {exam.countries && (
                    <div className="exam-card-countries">
                      {(() => {
                        try {
                          const countries = typeof exam.countries === 'string' 
                            ? JSON.parse(exam.countries) 
                            : exam.countries;
                          if (Array.isArray(countries) && countries.length > 0) {
                            return countries.join(', ');
                          }
                        } catch (e) {}
                        return null;
                      })()}
                    </div>
                  )}
                </div>
                <div className="exam-card-actions">
                  <button
                    className="exam-card-view-btn"
                    onClick={(e) => handleViewBooks(e, exam.id)}
                  >
                    View Books
                  </button>
                  <button
                    className="exam-card-chat-btn"
                    onClick={(e) => handleStartChat(e, exam.id)}
                  >
                    Start Chat
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mobile Bottom Menu */}
      <nav className="dashboard-nav mobile-nav">
        {menuItems.map((item) => (
          <button
            key={item.id}
            className={`nav-menu-item ${activeMenu === item.id ? 'active' : ''}`}
            onClick={() => handleMenuClick(item.id)}
          >
            <span className="nav-menu-icon">{item.icon}</span>
            <span className="nav-menu-label">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default ExamsPage;

